import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ReadingPart3 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        setAnswers(migratedAnswers);
        setIsHydrated(true);
        return;
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const {
    image_url,
    word_bank,
    reading_text = '',
    title_question
  } = exerciseData.content_data;

  const gapFillQuestions = exerciseData.questions.filter(q => q.order_index >= 0 && q.order_index <= 5);
  const finalQuestion = exerciseData.questions.find(q => q.order_index === 6);
  const example = exerciseData.questions.find(q => q.order_index === 0);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleGapChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleFinalAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleWordChipClick = (word) => {
    const firstEmptyInput = gapFillQuestions.find(q => q.order_index > 0 && !answers[q.id]);
    if (firstEmptyInput) {
      handleGapChange(firstEmptyInput.id, word);
    }
  };

  return (
    <div className="w-full h-full flex flex-col p-4 md:px-10">
      {image_url && (
        <div className="bg-white dark:bg-card-dark p-2 rounded-2xl shadow-soft border-2 border-white dark:border-card-dark overflow-hidden shrink-0 mb-6 mx-auto w-max animate-fade-in-down">
          <img
            src={`${ASSET_BASE_URL}${image_url}`}
            alt="Exercise illustration"
            className="mx-auto rounded-xl w-auto max-w-full max-h-48 object-contain shadow-sm"
          />
        </div>
      )}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="space-y-6">
          <div className="exercise-story-box !mt-0 p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40">
            <div className="space-y-4 exercise-body-text text-base md:text-lg leading-relaxed">
              {reading_text.split(/\n+/).filter(p => p.trim() !== '').map((para, paraIndex) => {
                const parts = para.split(/(\{\{\d\}\})/g);
                return (
                  <p key={paraIndex} className="leading-snug text-slate-700 dark:text-slate-300">
                    {parts.map((part, index) => {
                      const match = part.match(/\{\{(\d)\}\}/);
                      if (match) {
                        const qNumber = parseInt(match[1], 10);
                        if (qNumber === 0) {
                          return <span key={index} className="exercise-example-text mx-1 font-bold text-primary-blue underline decoration-2 underline-offset-4">{example?.accepted_answers?.[0]}</span>;
                        }
                        const question = gapFillQuestions.find(q => q.order_index === qNumber);
                        if (question) {
                          const isAnswerCorrect = isReviewMode && (question.is_correct === true || question.is_correct === 'true');
                          const showIncorrectAnswerInReview = isReviewMode && !isAnswerCorrect;
                          return (
                            <React.Fragment key={index}>
                              <strong className="exercise-gap-number align-middle mx-1 text-slate-400">({qNumber})</strong>
                              {showIncorrectAnswerInReview ? (
                                <span className="inline-flex items-center bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded-lg border border-red-100 dark:border-red-800 mx-1 align-middle max-w-[200px]">
                                  {question.user_answer?.answer
                                    ? <span className="text-red-500 line-through decoration-2 mr-2 font-bold truncate">{question.user_answer.answer}</span>
                                    : <span className="text-gray-400 mr-2 font-bold">__</span>
                                  }
                                  <span className="text-green-600 font-bold shrink-0">{question.accepted_answers[0]}</span>
                                </span>
                              ) : (
                                <input
                                  className={`exercise-input-field exercise-input-text !w-[110px] text-center mx-1 align-middle text-sm px-1 py-1 font-bold ${showHint ? 'hint-input' : ''}`}
                                  type="text"
                                  autoComplete="off"
                                  value={(showHint || isAnswerCorrect) ? question.accepted_answers[0] : (answers[question.id] || '')}
                                  onChange={(e) => handleGapChange(question.id, e.target.value)}
                                  readOnly={showHint || isReviewMode || !isHydrated}
                                  disabled={isReviewMode || !isHydrated}
                                />
                              )}
                              {isReviewMode && (isAnswerCorrect
                                ? <span className="correct-checkmark ml-1 align-middle scale-75">✓</span>
                                : <span className="incorrect-checkmark ml-1 align-middle scale-75">✗</span>
                              )}
                              {isReviewMode && (
                                <span className="ml-1 align-middle inline-flex"><QuestionLearnButton question={question} size={18} /></span>
                              )}
                            </React.Fragment>
                          );
                        }
                      }
                      return part;
                    })}
                  </p>
                );
              })}
            </div>
          </div>

          {title_question && finalQuestion && (
            <div className="exercise-card p-6 md:p-8 shadow-sm border border-slate-100 rounded-3xl bg-white dark:bg-slate-800/40">
              <h3 className="font-display font-bold text-base md:text-lg text-slate-800 dark:text-white mb-6 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-hupath-scout/10 flex items-center justify-center text-hupath-scout shrink-0">
                  <span className="material-symbols-outlined text-sm">edit</span>
                </span>
                <span>{title_question.instruction || "(6) Now choose the best name for the story:"}</span>
              </h3>
              <div className="grid grid-cols-1 gap-2">
                {(title_question.options || finalQuestion.options || []).map((option, idx) => {
                  const optionValue = typeof option === 'string' ? option : option.text;
                  const isCorrect = finalQuestion.is_correct === true || finalQuestion.is_correct === 'true';
                  const isThisOptionCorrect = finalQuestion.accepted_answers?.includes(optionValue);

                  // If showHint is active, the correct answer is considered "selected" for display purposes
                  const isSelected = showHint
                    ? isThisOptionCorrect
                    : (isReviewMode && isCorrect ? isThisOptionCorrect : answers[finalQuestion.id] === optionValue);

                  const isIncorrect = isReviewMode && !isCorrect && answers[finalQuestion.id] === optionValue && !isThisOptionCorrect;

                  return (
                    <div
                      key={idx}
                      onClick={() => !isReviewMode && !showHint && isHydrated && handleFinalAnswerChange(finalQuestion.id, optionValue)}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${isSelected
                        ? (showHint
                          ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 shadow-sm'
                          : 'border-primary-blue bg-blue-50 dark:bg-blue-900/20 shadow-sm')
                        : 'border-slate-100 dark:border-slate-700 hover:border-blue-200 bg-white dark:bg-slate-800'
                        } ${isReviewMode && isThisOptionCorrect ? 'border-green-500 bg-green-50/10 dark:bg-green-900/10' : ''} ${isIncorrect ? 'border-red-500 bg-red-50/10 dark:bg-red-900/10' : ''} ${showHint ? 'cursor-default' : ''}`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${isSelected
                        ? (showHint ? 'bg-amber-500 border-amber-500 text-white' : 'bg-primary-blue border-primary-blue text-white')
                        : 'border-slate-300 dark:border-slate-600'
                        }`}>
                        {isSelected && <span className="material-symbols-outlined text-[14px] font-bold">check</span>}
                      </div>
                      <span className={`font-body font-bold text-sm ${isSelected
                        ? (showHint ? 'text-amber-700 dark:text-amber-400' : 'text-primary-blue')
                        : 'text-slate-700 dark:text-slate-200'
                        }`}>
                        {optionValue.replace(/\(Example\)/gi, '').trim()}
                      </span>
                      {isReviewMode && isThisOptionCorrect && <span className="correct-checkmark ml-auto scale-75">✓</span>}
                      {isReviewMode && isIncorrect && <span className="incorrect-checkmark ml-auto scale-75">✗</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReadingPart3;